import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { LoaderCircle, Unlink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import ItemPicker from '../../cost-control/components/ItemPicker'
import { WRITE, useCostWriteQueue } from '../../cost-control/hooks/useCostWriteQueue'
import { compareMoneyStrings } from '../../cost-control/lib/contracts'
import { formatMoney, moneyLocale } from '../../cost-control/lib/money'
import { buildTree } from '../../cost-control/lib/tree'
import type { CostControlView, TenderCostBudget } from '../../cost-control/types'

const NO_EXCLUDE: string[] = []
const percent = (v: number, locale: string) => `${v > 0 ? '+' : ''}${v.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`

interface Props {
  tenderId: string
  projectId: string
  /** A running tender keeps its bids sealed; the server sends none and the card says why. */
  running: boolean
  className?: string
}

/**
 * Integrations spec §4.4: the tender's budget item against its bids, above the tender's tabs so it can be linked at any
 * time. Renders nothing when the query answers null (no
 * Cost Control right) or fails, so a procurement-only user never sees an error. Spec §4.2: compared with the revised
 * budget, not "remaining to commit".
 */
const TenderBudgetCard: React.FC<Props> = ({ tenderId, projectId, running, className }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { run } = useCostWriteQueue()
  const [setNode] = useMutation(Mutation.SET_TENDER_COST_NODE, WRITE)
  const [busy, setBusy] = useState(false)
  const { data, error } = useQuery<{ tenderCostBudget: TenderCostBudget | null }>(Query.TENDER_COST_BUDGET, {
    variables: { tenderId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const budget = data?.tenderCostBudget ?? null
  // The budget tree only for an editor: the item picker needs it.
  const { data: view } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    skip: !budget?.canEdit,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const tree = useMemo(() => buildTree(view?.costControl.nodes ?? []), [view])
  if (error || !budget) return null

  const money = (v: string) => formatMoney(v, { locale, currency: budget.currency, whole: true })
  const nodeLabel = budget.node ? (budget.node.path ? `${budget.node.path} › ${budget.node.name}` : budget.node.name) : undefined
  const save = async (nodeId: string | null) => {
    setBusy(true)
    try {
      await run((o) => setNode(o), { tenderId, nodeId }, [{ query: Query.TENDER_COST_BUDGET, variables: { tenderId } }])
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className={cn('border-0 shadow-sm', className)}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_tender_budget_title')}</CardTitle>
        {budget.canEdit && (
          <div className="flex items-center gap-2">
            <ItemPicker variant="select" withGroups tree={tree} excludeIds={NO_EXCLUDE} label={nodeLabel} disabled={busy} onPick={(id) => save(id)} />
            {budget.node && (
              <Button variant="ghost" size="sm" onClick={() => save(null)} disabled={busy}>
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
                {t('CostControl_tender_budget_unlink')}
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {!budget.node && <p className="text-sm text-muted-foreground">{t('CostControl_tender_budget_empty')}</p>}
        {budget.node && (
          <>
            {!budget.canEdit && <p className="text-sm">{nodeLabel}</p>}
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">{t('CostControl_col_revisedBudget')}</span>{' '}
                <span className="font-semibold tabular-nums">{money(budget.revisedBudget ?? '0')}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('CostControl_col_committed')}</span>{' '}
                <span className="font-semibold tabular-nums">{money(budget.committed ?? '0')}</span>
              </div>
            </div>
            {running ? (
              <p className="text-sm text-muted-foreground">{t('CostControl_tender_budget_running')}</p>
            ) : budget.bids.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('CostControl_tender_budget_no_bids')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 font-normal">{t('CostControl_tender_budget_bidder')}</th>
                    <th className="py-1 text-right font-normal">{t('CostControl_tender_budget_bid')}</th>
                    <th className="py-1 text-right font-normal">{t('CostControl_tender_budget_difference')}</th>
                  </tr>
                </thead>
                <tbody>
                  {budget.bids.map((b) => (
                    <tr key={b.bidderId} className="border-t">
                      <td className="py-1.5">{b.bidderName}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(b.amount)}</td>
                      <td className={cn('py-1.5 text-right tabular-nums', compareMoneyStrings(b.difference, '0') > 0 && 'text-destructive')}>
                        {money(b.difference)}{b.differencePercent !== null ? ` (${percent(b.differencePercent, locale)})` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
export default TenderBudgetCard
